<template>
  <div class="setup-root">
    <div class="setup-bg"></div>

    <!-- HEADER -->
    <div class="setup-header">
      <div class="setup-logo">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="#ff825d">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
        </svg>
        <span>Abitué</span>
      </div>
      <div class="setup-progress-wrap">
        <div class="setup-progress-track">
          <div
            class="setup-progress-fill"
            :style="{ width: progressoPct + '%' }"
          ></div>
        </div>
        <span class="setup-progress-txt"
          >Passo {{ step }} de {{ totalSteps }}</span
        >
      </div>
    </div>

    <div class="setup-body">
      <transition name="setup-slide" mode="out-in">
        <!-- STEP 1: BEM-VINDO -->
        <div v-if="step === 1" key="s1" class="setup-welcome">
          <div class="welcome-blob">🎉</div>
          <h1 class="welcome-title">Conta criada com sucesso!</h1>
          <p class="welcome-sub">
            Vamos configurar o teu primeiro restaurante em menos de 2 minutos.
          </p>
          <div class="welcome-features">
            <div class="wf-item">
              <span class="wf-dot"></span>Adiciona os teus restaurantes
            </div>
            <div class="wf-item">
              <span class="wf-dot"></span>Configura a integração POS
            </div>
            <div class="wf-item">
              <span class="wf-dot"></span>Começa a crescer
            </div>
          </div>
          <button class="btn-cta" @click="step = 2">
            Começar configuração
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
          <button class="btn-skip" @click="irParaDashboard">
            Configurar mais tarde
          </button>
        </div>

        <!-- STEP 2: RESTAURANTES (2 colunas) -->
        <div v-else-if="step === 2" key="s2" class="setup-main">
          <!-- Coluna esquerda -->
          <div class="setup-form-col">
            <div class="setup-form-header">
              <div class="sfc-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ff825d"
                  stroke-width="2"
                >
                  <path d="M3 2h18v4H3zM3 10h18v12H3z" />
                  <path d="M8 14h8M8 18h5" />
                </svg>
              </div>
              <div>
                <h2 class="sfc-title">Os teus restaurantes</h2>
                <p class="sfc-sub">Adiciona os restaurantes do teu negócio</p>
              </div>
            </div>

            <div v-if="restaurantes.length" class="rest-chips">
              <div v-for="(r, i) in restaurantes" :key="i" class="rest-chip">
                <div class="rest-chip-avatar">
                  {{ r.nome[0].toUpperCase() }}
                </div>
                <div class="rest-chip-info">
                  <span class="rest-chip-nome">{{ r.nome }}</span>
                  <span class="rest-chip-sub">{{
                    r.email || r.telefone || "Sem contacto"
                  }}</span>
                </div>
                <span
                  class="rest-chip-badge"
                  :class="
                    r.app_key && r.app_secret && r.client_id ? 'ok' : 'warn'
                  "
                >
                  {{
                    r.app_key && r.app_secret && r.client_id
                      ? "POS ✓"
                      : "Sem POS"
                  }}
                </span>
                <button
                  class="rest-chip-del"
                  @click="restaurantes.splice(i, 1)"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div
              v-if="!formAberto && !restaurantes.length"
              class="rest-empty-prompt"
              @click="formAberto = true"
            >
              <div class="rep-icon">+</div>
              <div>
                <p class="rep-title">Adicionar primeiro restaurante</p>
                <p class="rep-sub">Clica para preencher os dados</p>
              </div>
            </div>

            <button
              v-else-if="!formAberto"
              class="btn-add-inline"
              @click="formAberto = true"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              Adicionar outro restaurante
            </button>

            <div v-if="formAberto" class="rest-form">
              <div class="rf-section-title">Informações do restaurante</div>

              <div class="rf-field rf-field-full">
                <label>Nome *</label>
                <input
                  v-model="novoRest.nome"
                  class="rf-inp"
                  :class="{ err: erros.nome }"
                  placeholder="Ex: Restaurante Central"
                  @input="erros.nome = ''"
                />
                <p v-if="erros.nome" class="rf-err">{{ erroNome }}</p>
              </div>

              <div class="rf-row">
                <div class="rf-field">
                  <label>Email</label>
                  <input
                    v-model="novoRest.email"
                    class="rf-inp"
                    type="email"
                    placeholder="geral@restaurante.pt"
                  />
                </div>
                <div class="rf-field">
                  <label>Telefone</label>
                  <input
                    v-model="novoRest.telefone"
                    class="rf-inp"
                    placeholder="912 345 678"
                  />
                </div>
              </div>

              <div class="rf-field rf-field-full">
                <label>Morada</label>
                <input
                  v-model="novoRest.morada"
                  class="rf-inp"
                  placeholder="Rua..., Nº, Cidade"
                />
              </div>

              <div class="rf-section-title rf-section-title--pos">
                Integração {{ posLabel }}
                <span class="rf-required">obrigatório</span>
              </div>

              <div class="rf-row rf-row-3">
                <div class="rf-field">
                  <label>App Key</label>
                  <input
                    v-model="novoRest.app_key"
                    class="rf-inp rf-mono"
                    :class="{ err: erros.integracao && !novoRest.app_key }"
                    placeholder="app_key..."
                    @input="resetTeste"
                  />
                </div>
                <div class="rf-field">
                  <label>App Secret</label>
                  <input
                    v-model="novoRest.app_secret"
                    class="rf-inp rf-mono"
                    :class="{ err: erros.integracao && !novoRest.app_secret }"
                    type="password"
                    placeholder="••••••••"
                    @input="resetTeste"
                  />
                </div>
                <div class="rf-field">
                  <label>Client ID</label>
                  <input
                    v-model="novoRest.client_id"
                    class="rf-inp rf-mono"
                    :class="{ err: erros.integracao && !novoRest.client_id }"
                    placeholder="client_..."
                    @input="resetTeste"
                  />
                </div>
              </div>

              <!-- Meta / WhatsApp -->
              <div class="rf-section-title rf-section-title--pos">
                Meta / WhatsApp
                <span class="rf-opt-badge">opcional</span>
              </div>
              <div class="rf-field rf-field-full">
                <label>Nome do template</label>
                <input
                  v-model="novoRest.template_whatsapp"
                  class="rf-inp"
                  placeholder="ex: boas_vindas"
                />
                <p class="rf-hint">
                  Template enviado ao criar um novo cartão de cliente.
                </p>
              </div>

              <!-- Test connection -->
              <div class="rf-test-wrap">
                <button
                  class="btn-test"
                  :class="{
                    testing: testandoLigacao,
                    ok: testeOk === true,
                    fail: testeOk === false,
                  }"
                  :disabled="
                    !novoRest.app_key ||
                    !novoRest.app_secret ||
                    !novoRest.client_id ||
                    testandoLigacao
                  "
                  @click="testarLigacao"
                >
                  <span v-if="testandoLigacao" class="rf-spinner"></span>
                  <svg
                    v-else-if="testeOk === true"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  <svg
                    v-else-if="testeOk === false"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                  <svg
                    v-else
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  {{
                    testandoLigacao
                      ? "A testar..."
                      : testeOk === true
                      ? "Ligação OK!"
                      : testeOk === false
                      ? "Falhou — tentar novamente"
                      : "Testar ligação"
                  }}
                </button>
                <p
                  v-if="testeMensagem"
                  class="rf-test-msg"
                  :class="{ ok: testeOk, fail: testeOk === false }"
                >
                  {{ testeMensagem }}
                </p>
                <p v-if="erros.integracao" class="rf-err">
                  {{ erros.integracao }}
                </p>
              </div>

              <div class="rf-actions">
                <button class="btn-cancel" @click="cancelarForm">
                  Cancelar
                </button>
                <button class="btn-add" @click="adicionarRestaurante">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                  >
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  Adicionar
                </button>
              </div>
            </div>

            <div class="setup-footer">
              <button class="btn-back" @click="step = 1">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <path d="M19 12H5M12 5l-7 7 7 7" />
                </svg>
                Voltar
              </button>
              <button
                class="btn-cta"
                :disabled="!restaurantes.length || loading"
                @click="guardarRestaurantes"
              >
                <span v-if="loading" class="spinner"></span>
                <template v-else>
                  Guardar e continuar
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </template>
              </button>
            </div>
          </div>

          <!-- Coluna direita — preview -->
          <div class="setup-preview-col">
            <div class="preview-card">
              <div class="preview-header">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ff825d"
                  stroke-width="2"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                O teu progresso
              </div>
              <div class="preview-count">
                <span class="preview-num">{{ restaurantes.length }}</span>
                <span class="preview-label"
                  >restaurante{{ restaurantes.length !== 1 ? "s" : "" }}</span
                >
              </div>
              <div v-if="restaurantes.length" class="preview-list">
                <div
                  v-for="(r, i) in restaurantes"
                  :key="i"
                  class="preview-item"
                >
                  <div class="preview-avatar">
                    {{ r.nome[0].toUpperCase() }}
                  </div>
                  <span>{{ r.nome }}</span>
                </div>
              </div>
              <p v-else class="preview-empty">Ainda sem restaurantes.</p>
            </div>

            <div class="tip-card">
              <p class="tip-title">💡 Dica</p>
              <p class="tip-text">
                Podes configurar a integração POS mais tarde. O restaurante fica
                activo assim que a ligação estiver completa.
              </p>
            </div>
          </div>
        </div>

        <!-- STEP 3: CONCLUSÃO -->
        <div v-else-if="step === 3" key="s3" class="setup-done">
          <div class="done-check">
            <svg
              width="36"
              height="36"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ff825d"
              stroke-width="2.5"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </div>
          <h2 class="done-title">Configuração concluída!</h2>
          <p class="done-sub">Os teus restaurantes estão prontos.</p>

          <div v-if="semIntegracao.length" class="done-alert">
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#c07800"
              stroke-width="2"
            >
              <path
                d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
              />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <div>
              <strong>Integração em falta:</strong>
              <span v-for="r in semIntegracao" :key="r.id"> {{ r.nome }}</span>
              — configura em <em>Definições → Restaurantes</em>.
            </div>
          </div>

          <div class="done-rest-list">
            <div
              v-for="r in restaurantesCriados"
              :key="r.id"
              class="done-rest-item"
            >
              <div class="done-rest-avatar">{{ r.nome[0].toUpperCase() }}</div>
              <span class="done-rest-nome">{{ r.nome }}</span>
              <span
                class="done-badge"
                :class="r.integracao_completa ? 'ok' : 'warn'"
              >
                {{ r.integracao_completa ? "Integrado" : "Sem integração" }}
              </span>
            </div>
          </div>

          <button class="btn-cta btn-cta-full" @click="irParaDashboard">
            Ir para a Dashboard
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
  import api from "@/api";
  const emptyRest = () => ({
    nome: "",
    email: "",
    telefone: "",
    morada: "",
    app_key: "",
    app_secret: "",
    client_id: "",
  });
  export default {
    name: "Setup",
    data() {
      return {
        step: 1,
        totalSteps: 3,
        loading: false,
        formAberto: false,
        integracaoAberta: false,
        erroNome: "",
        posEscolhido: localStorage.getItem("abitue_pos") || "custom",
        restaurantes: [],
        restaurantesCriados: [],
        semIntegracao: [],
        novoRest: emptyRest(),
        testandoLigacao: false,
        testeOk: null,
        testeMensagem: "",
        erros: { nome: "", integracao: "" },
      };
    },
    computed: {
      progressoPct() {
        return ((this.step - 1) / (this.totalSteps - 1)) * 100;
      },
      posLabel() {
        return (
          { zonesoft: "Zonesoft", winrest: "Winrest", custom: "POS" }[
            this.posEscolhido
          ] || "POS"
        );
      },
    },
    methods: {
      cancelarForm() {
        this.formAberto = false;
        this.novoRest = emptyRest();
        this.erros = { nome: "", integracao: "" };
        this.testeOk = null;
        this.testeMensagem = "";
      },
      resetTeste() {
        this.testeOk = null;
        this.testeMensagem = "";
        this.erros.integracao = "";
      },
      adicionarRestaurante() {
        this.erros = { nome: "", integracao: "" };
        if (!this.novoRest.nome.trim()) {
          this.erros.nome = "Nome é obrigatório.";
          return;
        }
        if (
          !this.novoRest.app_key ||
          !this.novoRest.app_secret ||
          !this.novoRest.client_id
        ) {
          this.erros.integracao =
            "Integração obrigatória. Preenche as credenciais e testa a ligação.";
          return;
        }
        if (this.testeOk !== true) {
          this.erros.integracao =
            "Testa a ligação antes de adicionar o restaurante.";
          return;
        }
        this.restaurantes.push({ ...this.novoRest });
        this.cancelarForm();
      },
      async testarLigacao() {
        this.testandoLigacao = true;
        this.testeOk = null;
        this.testeMensagem = "";
        try {
          const { data } = await api.post("/restaurantes/test-connection", {
            app_key: this.novoRest.app_key,
            app_secret: this.novoRest.app_secret,
            client_id: this.novoRest.client_id,
          });
          this.testeOk = data.success;
          this.testeMensagem = data.message;
        } catch (err) {
          this.testeOk = false;
          this.testeMensagem =
            err.response?.data?.message || "Erro ao testar ligação.";
        } finally {
          this.testandoLigacao = false;
        }
      },
      async guardarRestaurantes() {
        if (!this.restaurantes.length) return;
        this.loading = true;
        try {
          const criados = [],
            semInt = [];
          for (const r of this.restaurantes) {
            const { data } = await api.post("/restaurantes", r);
            criados.push({
              id: data.id,
              nome: r.nome,
              integracao_completa: data.integracao_completa,
            });
            if (!data.integracao_completa)
              semInt.push({ id: data.id, nome: r.nome });
          }
          this.restaurantesCriados = criados;
          this.semIntegracao = semInt;
          this.$router._setupChecked = true;
          this.$router._temRestaurantes = true;
          this.step = 3;
        } catch (err) {
          this.$notify({
            type: "danger",
            title: "Erro",
            message:
              err.response?.data?.error || "Erro ao guardar restaurantes.",
          });
        } finally {
          this.loading = false;
        }
      },
      irParaDashboard() {
        this.$router.push("/dashboard");
      },
    },
  };
</script>

<style scoped lang="scss">
  $orange: #ff825d;
  $dark: #1a1210;
  $bg: #f5f2ef;
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
  .setup-root {
    min-height: 100vh;
    background: $bg;
    display: flex;
    flex-direction: column;
    font-family: "DM Sans", "Outfit", system-ui, sans-serif;
  }
  .setup-bg {
    position: fixed;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background: radial-gradient(
        ellipse at 70% -10%,
        rgba(255, 130, 93, 0.12) 0%,
        transparent 55%
      ),
      radial-gradient(
        ellipse at 0% 100%,
        rgba(255, 130, 93, 0.07) 0%,
        transparent 50%
      );
  }
  .setup-header {
    position: sticky;
    top: 0;
    z-index: 10;
    background: rgba(245, 242, 239, 0.92);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    display: flex;
    align-items: center;
    gap: 24px;
    padding: 14px 40px;
  }
  .setup-logo {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 18px;
    font-weight: 800;
    color: $dark;
    flex-shrink: 0;
  }
  .setup-progress-wrap {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .setup-progress-track {
    flex: 1;
    height: 4px;
    background: rgba(0, 0, 0, 0.08);
    border-radius: 2px;
    overflow: hidden;
  }
  .setup-progress-fill {
    height: 100%;
    background: $orange;
    border-radius: 2px;
    transition: width 0.4s ease;
  }
  .setup-progress-txt {
    font-size: 12px;
    color: #b0a8a0;
    font-weight: 600;
    white-space: nowrap;
  }
  .setup-body {
    flex: 1;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 56px 40px 80px;
    position: relative;
    z-index: 1;
  }

  /* WELCOME */
  .setup-welcome {
    max-width: 520px;
    width: 100%;
    text-align: center;
  }
  .welcome-blob {
    font-size: 64px;
    margin-bottom: 20px;
    filter: drop-shadow(0 8px 24px rgba(255, 130, 93, 0.25));
  }
  .welcome-title {
    font-size: 34px;
    font-weight: 900;
    color: $dark;
    letter-spacing: -0.6px;
    margin-bottom: 12px;
  }
  .welcome-sub {
    font-size: 16px;
    color: #7a736e;
    margin-bottom: 32px;
    line-height: 1.65;
  }
  .welcome-features {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 36px;
    text-align: left;
    max-width: 320px;
    margin-left: auto;
    margin-right: auto;
  }
  .wf-item {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    color: #5a534f;
    font-weight: 500;
  }
  .wf-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: $orange;
    flex-shrink: 0;
    box-shadow: 0 0 0 3px rgba(255, 130, 93, 0.15);
  }

  /* MAIN 2-col */
  .setup-main {
    display: grid;
    grid-template-columns: 1fr 300px;
    gap: 24px;
    width: 100%;
    max-width: 1020px;
    align-items: start;
  }
  .setup-form-col {
    background: #fff;
    border-radius: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04), 0 8px 32px rgba(0, 0, 0, 0.07);
    overflow: hidden;
  }
  .setup-form-header {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 28px 32px 20px;
    border-bottom: 1px solid #f0ece8;
  }
  .sfc-icon {
    width: 46px;
    height: 46px;
    background: #fff5f0;
    border-radius: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .sfc-title {
    font-size: 18px;
    font-weight: 800;
    color: $dark;
    margin: 0;
  }
  .sfc-sub {
    font-size: 13px;
    color: #b0a8a0;
    margin: 3px 0 0;
  }

  /* Chips */
  .rest-chips {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 20px 32px 0;
  }
  .rest-chip {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #faf8f6;
    border: 1.5px solid #f0ece8;
    border-radius: 12px;
  }
  .rest-chip-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: $orange;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    flex-shrink: 0;
  }
  .rest-chip-info {
    flex: 1;
    min-width: 0;
  }
  .rest-chip-nome {
    display: block;
    font-size: 14px;
    font-weight: 700;
    color: $dark;
  }
  .rest-chip-sub {
    display: block;
    font-size: 11.5px;
    color: #b0a8a0;
  }
  .rest-chip-badge {
    font-size: 11px;
    font-weight: 700;
    padding: 3px 9px;
    border-radius: 20px;
    white-space: nowrap;
    &.ok {
      background: #e8faf2;
      color: #0a7a4a;
    }
    &.warn {
      background: #fff8e8;
      color: #c07800;
    }
  }
  .rest-chip-del {
    background: none;
    border: none;
    cursor: pointer;
    color: #c0b8b0;
    padding: 4px;
    border-radius: 6px;
    display: flex;
    &:hover {
      background: #f0ece8;
      color: $dark;
    }
  }

  /* Empty prompt */
  .rest-empty-prompt {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 20px 32px;
    padding: 20px 24px;
    border: 2px dashed #e0dbd5;
    border-radius: 14px;
    cursor: pointer;
    transition: all 0.15s;
    &:hover {
      border-color: $orange;
      background: rgba(255, 130, 93, 0.02);
    }
  }
  .rep-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #fff0eb;
    color: $orange;
    font-size: 22px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .rep-title {
    font-size: 14px;
    font-weight: 700;
    color: $dark;
    margin: 0;
  }
  .rep-sub {
    font-size: 12px;
    color: #b0a8a0;
    margin: 2px 0 0;
  }
  .btn-add-inline {
    display: flex;
    align-items: center;
    gap: 7px;
    margin: 12px 32px 0;
    padding: 10px 16px;
    border: 1.5px dashed #d0c8c0;
    border-radius: 10px;
    background: transparent;
    font-size: 13.5px;
    font-weight: 600;
    color: #7a736e;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.15s;
    &:hover {
      border-color: $orange;
      color: $orange;
    }
  }

  /* Form */
  .rest-form {
    padding: 20px 32px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    border-top: 1px solid #f0ece8;
    margin-top: 16px;
  }
  .rf-section-title {
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #b0a8a0;
  }
  .rf-field {
    display: flex;
    flex-direction: column;
    gap: 5px;
    label {
      font-size: 12px;
      font-weight: 700;
      color: #7a736e;
    }
  }
  .rf-field-full {
    width: 100%;
  }
  .rf-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .rf-row-3 {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px;
  }
  .rf-inp {
    height: 46px;
    border: 1.5px solid #e8e4e0;
    border-radius: 11px;
    padding: 0 14px;
    font-size: 14px;
    color: $dark;
    outline: none;
    font-family: inherit;
    background: #fdfcfb;
    transition: all 0.15s;
    &:focus {
      border-color: $orange;
      background: #fff;
      box-shadow: 0 0 0 3px rgba(255, 130, 93, 0.1);
    }
    &.err {
      border-color: #e53e3e;
    }
    &::placeholder {
      color: #c8c0b8;
    }
  }
  .rf-mono {
    font-family: monospace;
    font-size: 12.5px;
  }
  .rf-err {
    font-size: 12px;
    color: #e53e3e;
    margin-top: 2px;
  }
  .rf-pos-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 11px 14px;
    background: #f7f4f1;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.15s;
    &:hover {
      background: #f0ece8;
    }
  }
  .rf-pos-left {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13.5px;
    font-weight: 600;
    color: #5a534f;
  }
  .rf-pos-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #d0c8c0;
    transition: background 0.2s;
    &.active {
      background: #0a7a4a;
    }
  }
  .rf-opt {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    background: #e8e4e0;
    color: #7a736e;
    padding: 2px 8px;
    border-radius: 20px;
  }
  .rf-pos-fields {
    padding: 4px 0;
  }
  .rf-actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    padding-top: 4px;
  }
  .btn-cancel {
    padding: 10px 18px;
    border: 1.5px solid #e8e4e0;
    border-radius: 10px;
    background: #fff;
    color: #7a736e;
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    &:hover {
      background: #f7f4f1;
    }
  }
  .btn-add {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 10px 20px;
    border: none;
    border-radius: 10px;
    background: $orange;
    color: #fff;
    font-size: 13.5px;
    font-weight: 700;
    cursor: pointer;
    font-family: inherit;
    box-shadow: 0 3px 10px rgba(255, 130, 93, 0.3);
    &:hover {
      background: #e86b45;
    }
  }

  /* Footer */
  .setup-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 32px 28px;
    border-top: 1px solid #f0ece8;
    margin-top: 8px;
  }
  .btn-back {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 11px 18px;
    border: 1.5px solid #e8e4e0;
    border-radius: 11px;
    background: #fff;
    color: #7a736e;
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    &:hover {
      background: #f7f4f1;
    }
  }
  .btn-cta {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    padding: 13px 26px;
    border: none;
    border-radius: 12px;
    background: $orange;
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    font-family: inherit;
    box-shadow: 0 4px 16px rgba(255, 130, 93, 0.35);
    transition: all 0.15s;
    &:hover:not(:disabled) {
      background: #e86b45;
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(255, 130, 93, 0.4);
    }
    &:disabled {
      opacity: 0.45;
      cursor: default;
      box-shadow: none;
      transform: none;
    }
  }
  .btn-cta-full {
    width: 100%;
    justify-content: center;
    margin-top: 8px;
  }
  .btn-skip {
    display: block;
    background: none;
    border: none;
    color: #b0a8a0;
    font-size: 13px;
    cursor: pointer;
    font-family: inherit;
    margin: 14px auto 0;
    &:hover {
      color: #7a736e;
    }
  }

  /* Preview col */
  .setup-preview-col {
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: sticky;
    top: 88px;
  }
  .preview-card {
    background: #fff;
    border-radius: 16px;
    padding: 22px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04), 0 6px 24px rgba(0, 0, 0, 0.06);
  }
  .preview-header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    font-weight: 700;
    color: #b0a8a0;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 16px;
  }
  .preview-count {
    text-align: center;
    margin-bottom: 16px;
  }
  .preview-num {
    display: block;
    font-size: 48px;
    font-weight: 900;
    color: $orange;
    line-height: 1;
    letter-spacing: -2px;
  }
  .preview-label {
    font-size: 13px;
    color: #b0a8a0;
  }
  .preview-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    border-top: 1px solid #f5f2ef;
    padding-top: 14px;
  }
  .preview-item {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13.5px;
    font-weight: 600;
    color: $dark;
  }
  .preview-avatar {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: $orange;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
  }
  .preview-empty {
    text-align: center;
    padding: 12px 0;
    font-size: 13px;
    color: #c8c0b8;
  }
  .tip-card {
    background: rgba(255, 130, 93, 0.06);
    border: 1.5px solid rgba(255, 130, 93, 0.15);
    border-radius: 14px;
    padding: 16px 18px;
  }
  .tip-title {
    font-size: 13px;
    font-weight: 700;
    color: $orange;
    margin-bottom: 6px;
  }
  .tip-text {
    font-size: 13px;
    color: #7a736e;
    line-height: 1.6;
    margin: 0;
  }

  /* DONE */
  .setup-done {
    max-width: 540px;
    width: 100%;
    text-align: center;
  }
  .done-check {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    border: 2.5px solid $orange;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    background: #fff5f0;
  }
  .done-title {
    font-size: 28px;
    font-weight: 900;
    color: $dark;
    margin-bottom: 8px;
    letter-spacing: -0.4px;
  }
  .done-sub {
    font-size: 15px;
    color: #7a736e;
    margin-bottom: 24px;
  }
  .done-alert {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background: #fff8e8;
    border: 1.5px solid #f5dc8a;
    border-radius: 12px;
    padding: 14px 16px;
    font-size: 13.5px;
    color: #6b5500;
    text-align: left;
    margin-bottom: 20px;
    svg {
      flex-shrink: 0;
      margin-top: 2px;
    }
  }
  .done-rest-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 24px;
  }
  .done-rest-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #fff;
    border: 1.5px solid #f0ece8;
    border-radius: 12px;
    text-align: left;
  }
  .done-rest-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: $orange;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    flex-shrink: 0;
  }
  .done-rest-nome {
    flex: 1;
    font-size: 14px;
    font-weight: 700;
    color: $dark;
  }
  .done-badge {
    font-size: 12px;
    font-weight: 700;
    padding: 3px 9px;
    border-radius: 20px;
    &.ok {
      background: #e8faf2;
      color: #0a7a4a;
    }
    &.warn {
      background: #fff8e8;
      color: #c07800;
    }
  }

  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  .setup-slide-enter-active {
    transition: all 0.25s cubic-bezier(0.22, 0.68, 0, 1.2);
  }
  .setup-slide-leave-active {
    transition: all 0.18s ease;
  }
  .setup-slide-enter {
    opacity: 0;
    transform: translateX(24px);
  }
  .setup-slide-leave-to {
    opacity: 0;
    transform: translateX(-16px);
  }
  @media (max-width: 860px) {
    .setup-main {
      grid-template-columns: 1fr;
    }
    .setup-preview-col {
      position: static;
    }
    .setup-body {
      padding: 32px 16px 60px;
    }
    .setup-header {
      padding: 12px 20px;
    }
  }

  .rf-section-title--pos {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .rf-required {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    background: #fff0eb;
    color: #ff825d;
    padding: 2px 8px;
    border-radius: 20px;
  }
  .rf-test-wrap {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .btn-test {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    border: 1.5px solid #e8e4e0;
    border-radius: 10px;
    background: #fff;
    color: #5a534f;
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.15s;
    align-self: flex-start;
    &:hover:not(:disabled) {
      border-color: #ff825d;
      color: #ff825d;
    }
    &:disabled {
      opacity: 0.45;
      cursor: default;
    }
    &.testing {
      border-color: #e8e4e0;
      color: #b0a8a0;
    }
    &.ok {
      border-color: #0a7a4a;
      background: #e8faf2;
      color: #0a7a4a;
    }
    &.fail {
      border-color: #e53e3e;
      background: #fff8f8;
      color: #e53e3e;
    }
  }
  .rf-test-msg {
    font-size: 12.5px;
    font-weight: 500;
    margin: 0;
    &.ok {
      color: #0a7a4a;
    }
    &.fail {
      color: #e53e3e;
    }
  }
  .rf-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(0, 0, 0, 0.12);
    border-top-color: #ff825d;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }

  .rf-opt-badge {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    background: #e8e4e0;
    color: #7a736e;
    padding: 2px 8px;
    border-radius: 20px;
  }
  .rf-hint {
    font-size: 11.5px;
    color: #b0a8a0;
    margin-top: 4px;
  }
</style>
